import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import axios from 'axios';

interface CustomerUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  company_name: string;
  entity_id: number;
}

interface CustomerAuthContextType {
  customer: CustomerUser | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface RegisterData {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  entity_id?: number;
}

const CustomerAuthContext = createContext<CustomerAuthContextType | undefined>(undefined);

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export function CustomerAuthProvider({ children }: { children: ReactNode }) {
  const [customer, setCustomer] = useState<CustomerUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('customer_token');
    const storedCustomer = localStorage.getItem('customer_user');

    if (storedToken && storedCustomer) {
      setToken(storedToken);
      setCustomer(JSON.parse(storedCustomer));
    }
    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/customer-auth/login`, {
        email,
        password,
      });

      const { token: authToken, user } = response.data;
      
      setToken(authToken);
      setCustomer(user);
      localStorage.setItem('customer_token', authToken);
      localStorage.setItem('customer_user', JSON.stringify(user));
    } catch (error: any) {
      const message = error.response?.data?.error || 'Login failed';
      throw new Error(message);
    }
  };

  const register = async (data: RegisterData): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await axios.post(`${API_BASE_URL}/customer-auth/register`, data);
      return {
        success: true,
        message: response.data.message || 'Registration successful. Your account is pending approval.'
      };
    } catch (error: any) {
      const message = error.response?.data?.error || 'Registration failed';
      throw new Error(message);
    }
  };

  const logout = () => {
    setToken(null);
    setCustomer(null);
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_user');
    window.location.href = '/portal/login';
  };

  return (
    <CustomerAuthContext.Provider
      value={{
        customer,
        token,
        login,
        register,
        logout,
        isAuthenticated: !!token && !!customer,
        isLoading,
      }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
}

export function useCustomerAuth() {
  const context = useContext(CustomerAuthContext);
  if (context === undefined) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
}
